<template>
  <component
    :is="embedded ? 'div' : 'v-card'"
    :class="rootClass"
  >
    <div
      v-if="!embedded"
      class="d-flex flex-column"
    >
      <v-toolbar
        dense
        flat
      >
        <v-btn
          v-if="!embedded"
          class="mr-5"
          title="Back to ToxPrints Home"
          aria-label="Back to ToxPrints Home"
          @click="$router.push('/toxprints')"
          v-b-tooltip
        >
          <v-icon>mdi-arrow-up</v-icon>
        </v-btn>

        <v-btn-toggle
          v-if="assays.length"
          dense
          class="mr-5"
        >
          <v-btn
            title="Assays view"
            @click="view = 0"
            v-b-tooltip
            aria-label="Assays view"
          >
            <v-icon>mdi-table-column-plus-after</v-icon>
          </v-btn>
          <v-btn
            title="Assay enrichments view"
            @click="view = 1"
            v-b-tooltip
            aria-label="Assay enrichments view"
          >
            <v-icon>mdi-table-column-plus-before</v-icon>
          </v-btn>
        </v-btn-toggle>

        <v-chip
          v-if="!pending"
          class="ma-1"
          title="The global ToxPrint-ToxCast assay enrichment table contains a total of 446/729 ToxPrints found to be enriched in 796/1614 ToxCast/Tox21 assays (invitrodb_v3_3), where each ToxCast/Tox21 assay is assigned to one of 22 assay categories. This section allows user to toggle views and filter the global enrichment table by ToxPrint or assay category."
          v-b-tooltip
          label
        >
          {{ assays.length }} / {{ total }}
          {{ view === 0 ? 'assays' : 'toxprints' }}
        </v-chip>

        <v-spacer />

        <v-chip
          v-if="category"
          class="ma-1"
          close
          @click:close="category = null"
        >
          {{ category }}
        </v-chip>
        <v-chip
          v-if="label"
          class="ma-1"
          close
          @click:close="label = null"
        >
          {{ label }}
        </v-chip>

        <v-spacer />

        <v-switch
          v-if="assays.length && view === 0"
          v-model="showBrief"
          class="mt-5 ml-4"
          label="Brief"
          title="Show data in a brief format"
        />

        <v-switch
          v-if="assays.length && view === 0 && !showBrief"
          v-model="showTable"
          class="mt-5 ml-4"
          label="Table"
          title="Show data in a tabular way rather than tile"
        />

        <v-switch
          v-if="assays.length && view === 0 && !showBrief"
          v-model="showLabels"
          class="mt-5 ml-4"
          label="Labels"
          title="Show ToxPrint label instead of the image"
        />

        <v-switch
          v-if="assays.length && view === 0 && !showTable"
          v-model="showDetails"
          class="mt-5 ml-4"
          label="Details"
          title="Show details"
        />

        <OptionsDropdown
          v-if="global && assays.length"
          :items="exportItems"
          :loading="downloading"
          title="Export to CSV or Excel"
          :disabled="pending"
          class="ml-2"
        />
      </v-toolbar>

      <v-progress-linear
        v-if="pending"
        indeterminate
      />
    </div>

    <v-progress-linear
      v-else-if="pending"
      indeterminate
    />

    <div class="d-flex flex-column assays-table-region">
      <v-data-table
        id="toxprints-assays-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 30, 50, -1],
        }"
        :hide-default-footer="assays.length <= 10"
        :headers="headers"
        :items="assays"
        :items-per-page="10"
      >
        <template #[`item.category`]="{ item }">
          <div style="height: 100%; margin-top: 1em">
            <span v-if="category">
              {{ item.category }}
            </span>

            <v-btn
              v-else
              small
              text
              color="primary"
              @click="category = item.category"
              class="p-0"
              :aria-label="item.category"
            >
              {{ item.category }}
            </v-btn>
          </div>
        </template>

        <template #[`item.name`]="{ item }">
          <div class="assay-name-cell">
            <v-btn
              v-if="global"
              small
              text
              color="primary"
              class="p-0 assay-name-link"
              :href="`https://comptox.epa.gov/dashboard/assay_endpoints/${encodeURIComponent(item.name)}`"
              target="epa"
              :aria-label="item.name"
              :title="item.name"
              v-b-tooltip
            >
              {{ item.name }}
            </v-btn>

            <v-btn
              v-else
              :to="`/toxprints/assays/${encodeURIComponent(item.name)}`"
              small
              text
              class="p-0 assay-name-link"
              color="primary"
              :aria-label="item.name"
              :title="item.name"
              v-b-tooltip
            >
              {{ item.name }}
            </v-btn>
          </div>
        </template>

        <template #[`item.actives`]="{ item }">
          <div style="height: 100%; margin-top: 1em">
            {{ item.actives }}
          </div>
        </template>

        <template #[`item.total`]="{ item }">
          <div style="height: 100%; margin-top: 1em">
            {{ item.total }}
          </div>
        </template>

        <template #[`item.enriched`]="{ item }">
          <div style="height: 100%; margin-top: 1em">
            {{ item.enriched }}
          </div>
        </template>

        <template #[`item.metrics`]="{ item: row }">
          <div v-if="showTable">
            <v-data-table
              hide-default-footer
              :headers="headers4"
              :items="row.metrics"
              :items-per-page="-1"
              dense
            >
              <template #[`item.label`]="{ item }">
                <div v-if="showLabels">
                  <v-btn
                    :id="`${row.id}-${item.ordinal}`"
                    text
                    small
                    color="primary"
                    class="pa-0"
                    @click="label = item.label"
                    :class="{ shadered: item.label === label }"
                    :aria-label="item.label"
                  >
                    {{ item.label }}
                  </v-btn>
                  <b-popover
                    :target="`${row.id}-${item.ordinal}`"
                    triggers="hover"
                  >
                    <img
                      :src="`${TOXPRINTS_URL}/render?id=${item.ordinal}`"
                      alt=""
                    />
                  </b-popover>
                </div>
                <div v-else>
                  <button
                    type="button"
                    @click="label = item.label"
                    class="image-action-button"
                    :aria-label="item.label"
                  >
                    <img
                      :src="`${TOXPRINTS_URL}/render?id=${item.ordinal}`"
                      :alt="item.label"
                      class="shadowed-on-hover"
                      :class="{ shadered: item.label === label }"
                      :title="item.label"
                      v-b-tooltip
                    />
                  </button>
                </div>
              </template>

              <template
                v-for="field in ['OR', 'PV1', 'PV2']"
                #[`item.${field}`]="{ item }"
              >
                <span
                  v-if="item[field] != null"
                  :key="field"
                >
                  {{ item[field] | number('0.00') }}
                </span>
              </template>
            </v-data-table>
          </div>
          <div
            v-else
            class="d-flex flex-wrap gap-2"
          >
            <div
              v-for="(txp, i) in row.metrics"
              :key="i"
              class="d-flex flex-column align-center ma-1 border"
            >
              <div v-if="showLabels">
                <v-btn
                  :id="`${row.id}-${txp.ordinal}`"
                  text
                  small
                  color="primary"
                  class="pa-0"
                  @click="label = txp.label"
                  :aria-label="txp.label"
                >
                  {{ txp.label }}
                </v-btn>
                <b-popover
                  :target="`${row.id}-${txp.ordinal}`"
                  triggers="hover"
                >
                  <img
                    :src="`${TOXPRINTS_URL}/render?id=${txp.ordinal}`"
                    alt=""
                  />
                </b-popover>
              </div>
              <div v-else>
                <button
                  type="button"
                  @click="label = txp.label"
                  class="image-action-button"
                  :aria-label="txp.label"
                >
                  <img
                    :src="`${TOXPRINTS_URL}/render?id=${txp.ordinal}`"
                    alt="txp.label"
                    class="shadowed-on-hover"
                    :class="{ shadered: txp.label === label }"
                    :title="txp.label"
                    v-b-tooltip
                  />
                </button>
              </div>
              <div v-if="showDetails">
                <span v-if="txp.OR">
                  <span style="font-weight: bolder">OR</span>
                  : {{ txp.OR }},
                </span>
                <span>
                  <span style="font-weight: bolder">pval</span>
                  : {{ txp.PV1 }},
                </span>
                <span>
                  <span style="font-weight: bolder">TP</span>
                  : {{ txp.TP }}
                </span>
              </div>
            </div>
          </div>
        </template>

        <template #[`item.label`]="{ item }">
          <div style="height: 100%">
            <button
              type="button"
              class="image-action-button"
              @click="label = item.label"
              :aria-label="item.label"
            >
              <img
                :src="`${TOXPRINTS_URL}/render?id=${item.ordinal}`"
                alt=""
                class="shadowed-on-hover"
                :class="{ shadered: item.label === label }"
                :title="item.label"
                v-b-tooltip
              />
            </button>
          </div>
        </template>

        <template #[`item.count`]="{ item }">
          <div style="height: 100%">
            {{ item.count }}
          </div>
        </template>

        <template #[`item.assays`]="{ item }">
          <div style="height: 100%">
            <v-data-table
              class="nested-assays-table"
              hide-default-footer
              :headers="headers3"
              :items="item.assays"
              :items-per-page="-1"
              dense
            >
              <template #[`item.name`]="{ item }">
                <div class="assay-name-cell assay-name-cell--nested">
                  <a
                    v-if="global"
                    :href="`https://comptox.epa.gov/dashboard/assay_endpoints/${item.name}`"
                    target="epa"
                    class="assay-name-anchor"
                    :title="item.name"
                  >
                    {{ item.name }}
                  </a>
                  <b-link
                    v-else
                    :to="`/toxprints/assays/${encodeURIComponent(item.name)}`"
                    class="assay-name-anchor"
                    :title="item.name"
                  >
                    {{ item.name }}
                  </b-link>
                </div>
              </template>

              <template #[`item.category`]="{ item }">
                <span v-if="category">
                  {{ item.category }}
                </span>
                <button
                  v-else
                  type="button"
                  @click="category = item.category"
                  class="text-action-button"
                >
                  {{ item.category }}
                </button>
              </template>

              <template
                v-for="field in ['OR', 'PV1', 'PV2']"
                #[`item.${field}`]="{ item }"
              >
                <span
                  v-if="item[field] != null"
                  :key="field"
                >
                  {{ item[field] | number('0.00') }}
                </span>
              </template>
            </v-data-table>
          </div>
        </template>
      </v-data-table>
    </div>
  </component>
</template>

<script>
import { download, getDatedFilename, TOXPRINTS_URL } from '@/main'
import { showErrorToast } from '@/utils/toast'
import OptionsDropdown from '@/components/OptionsDropdown.vue'

export default {
  components: { OptionsDropdown },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    headers1: [
      { text: 'Category', value: 'category', width: '10rem' },
      { text: 'Assay Name', value: 'name', width: '18rem' },
      { text: 'Actives', value: 'actives', width: '6rem' },
      { text: 'Total', value: 'total', width: '6rem' },
      { text: 'Enriched', value: 'enriched', width: '6rem' },
    ],
    headers2: [
      { text: 'ToxPrint', value: 'label' },
      { text: 'Count', value: 'count' },
      { text: 'Assays', value: 'assays', width: '80%', sortable: false },
    ],
    headers3: [
      { text: 'Assay Name', value: 'name', width: '18rem' },
      { text: 'Category', value: 'category' },
      { text: 'Actives', value: 'actives' },
      { text: 'Total', value: 'total' },
      { text: 'OR', value: 'OR' },
      { text: 'pval', value: 'PV1' },
      { text: 'TP', value: 'TP' },
      { text: 'FN', value: 'FN' },
      { text: 'FP', value: 'FP' },
      { text: 'TN', value: 'TN' },
    ],
    headers4: [
      { text: 'ToxPrint', value: 'label' },
      { text: 'OR', value: 'OR' },
      { text: 'pval', value: 'PV1' },
      { text: 'TP', value: 'TP' },
      { text: 'FN', value: 'FN' },
      { text: 'FP', value: 'FP' },
      { text: 'TN', value: 'TN' },
    ],
    assays: [],
    total: 0,
    showBrief: false,
    showTable: true,
    showLabels: true,
    showDetails: false,
    pending: false,
    downloading: false,
  }),

  computed: {
    TOXPRINTS_URL() {
      return TOXPRINTS_URL
    },
    query() {
      return this.$route.query
    },
    view: {
      get() {
        return this.$route.query.view != null ? parseInt(this.$route.query.view) : 0
      },
      set(val) {
        this.$router.push(this.getUrl(val, this.category, this.label))
      },
    },
    category: {
      get() {
        return this.$route.query.category
      },
      set(val) {
        this.$router.push(this.getUrl(this.view, val, this.label))
      },
    },
    label: {
      get() {
        return this.$route.query.label
      },
      set(val) {
        this.$router.push(this.getUrl(this.view, this.category, val))
      },
    },
    global() {
      return this.$route.path.indexOf('global_') !== -1
    },
    headers() {
      return this.view === 0
        ? this.showBrief
          ? this.headers1
          : [
              ...this.headers1,
              {
                text: 'ToxPrints',
                value: 'metrics',
                width: '80%',
                sortable: false,
              },
            ]
        : this.headers2
    },
    rootClass() {
      return this.embedded ? 'd-flex flex-column w-100 h-100 toxprints-embedded-panel' : 'ma-1 d-flex flex-column h-100'
    },
    exportItems() {
      return [
        {
          title: 'Export to CSV',
          icon: 'mdi-file-delimited-outline',
          onClick: () => this.exportFile('CSV'),
        },
        {
          title: 'Export to XLSX',
          icon: 'mdi-file-excel-outline',
          onClick: () => this.exportFile('XLSX'),
        },
      ]
    },
  },

  watch: {
    query() {
      this.getData()
    },
  },

  methods: {
    getUrl(view, category, label) {
      let url = `/toxprints/${this.global ? 'global_' : ''}assays`
      let params = []
      if (view) params.push(`view=${view}`)
      if (category) params.push(`category=${encodeURIComponent(category)}`)
      if (label) params.push(`label=${encodeURIComponent(label)}`)
      if (params.length) url += '?' + params.join('&')
      return url
    },
    exportFile(format) {
      format = format.toLowerCase()
      this.downloading = true
      this.$axios
        .post(
          `${TOXPRINTS_URL}/export_global`,
          {
            category: this.category,
            label: this.label,
            format: format.toUpperCase(),
            sheet: format === 'csv' ? this.view : null,
          },
          { responseType: 'arraybuffer' },
        )
        .then((response) => {
          let filename = getDatedFilename('global_assays', format)
          download(filename, response.data, format)
        })
        .catch((error) => {
          showErrorToast(error, { title: 'Error during export' })
        })
        .finally(() => {
          this.downloading = false
        })
    },

    getData() {
      let url = `${TOXPRINTS_URL}/${this.global ? 'global_' : ''}${this.view === 0 ? 'assays' : 'toxprints'}`
      if (this.category || this.label) {
        let params = []
        if (this.category) params.push({ key: 'category', value: this.category })
        if (this.label) params.push({ key: 'label', value: this.label })
        const paramsString = params.map((p) => `${p.key}=${encodeURIComponent(p.value)}`).join('&')
        url += `?${paramsString}`
      }
      this.pending = true
      this.$axios
        .get(url)
        .then((response) => {
          this.assays = response.data.assays
          this.total = response.data.total
          if (this.view === 0) this.assays.forEach((a) => (a.enriched = a.metrics.length))
        })
        .catch((error) => {
          showErrorToast(error)
        })
        .finally(() => {
          this.pending = false
        })
    },
  },

  mounted() {
    this.getData()
    this.showBrief = !this.global
    if (this.embedded) this.$emit('controller-ready', this)
  },

  beforeDestroy() {
    if (this.embedded) this.$emit('controller-ready', null)
  },
}
</script>

<style scoped>
.toxprints-embedded-panel {
  min-height: 0;
}

.assays-table-region {
  flex: 1 1 auto;
  min-height: 0;
}

::v-deep #toxprints-assays-table {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  width: 100%;
}

::v-deep #toxprints-assays-table > .v-data-table__wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

::v-deep #toxprints-assays-table > .v-data-table__wrapper > table {
  table-layout: fixed;
}

::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > thead > tr > th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
}

::v-deep #toxprints-assays-table > .v-data-footer {
  flex: 0 0 auto;
}

::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > thead > tr > th:nth-child(1),
::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > tbody > tr > td:nth-child(1) {
  width: 10rem;
  min-width: 10rem;
  max-width: 10rem;
}

::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > thead > tr > th:nth-child(2),
::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > tbody > tr > td:nth-child(2) {
  width: 18rem;
  min-width: 18rem;
  max-width: 18rem;
}

::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > thead > tr > th:nth-child(3),
::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > tbody > tr > td:nth-child(3),
::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > thead > tr > th:nth-child(4),
::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > tbody > tr > td:nth-child(4),
::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > thead > tr > th:nth-child(5),
::v-deep #toxprints-assays-table > .v-data-table__wrapper > table > tbody > tr > td:nth-child(5) {
  width: 6rem;
  min-width: 6rem;
  max-width: 6rem;
}

::v-deep .nested-assays-table > .v-data-table__wrapper > table > thead > tr > th:nth-child(1),
::v-deep .nested-assays-table > .v-data-table__wrapper > table > tbody > tr > td:nth-child(1) {
  width: 18rem;
  min-width: 18rem;
  max-width: 18rem;
}

::v-deep .nested-assays-table > .v-data-table__wrapper > table {
  table-layout: fixed;
}

.assay-name-cell {
  height: 100%;
  margin-top: 1em;
  width: 100%;
  max-width: 18rem;
}

.assay-name-cell--nested {
  margin-top: 0;
  max-width: 18rem;
}

::v-deep .assay-name-link {
  display: inline-flex;
  max-width: 100%;
}

::v-deep .assay-name-link .v-btn__content {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assay-name-anchor {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: top;
}

.image-action-button {
  background: transparent;
  border: 0;
  cursor: pointer;
  display: inline-block;
  line-height: 0;
  padding: 0;
}

.text-action-button {
  background: transparent;
  border: 0;
  color: #1873cd;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}
</style>
